import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import type {
  AiAnalysisBousaiResult,
  AiAnalysisWalkerResult,
  AiVerificationSummary,
  PostAiAnalysis,
  PostPhotoMeta,
  PostPhotoPrivacy,
  PostPhotoReplacement,
  PostReanalysisRequest,
  PostVisibility,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

/**
 * Posts moderation hook (admin 第 11 弾、Phase 5.4 REQ-4.3.9-13)。
 *
 * 旧 apps/admin/src/pages/PostsModerationPage.tsx (1179 行) の write フローを minimum scope で
 * 踏襲。本 hook は subscribe + visibility 変更 + 物理削除 helper を提供。
 *
 * 認可: firestore.rules
 *   - read: isDistrictAdmin(districtId) → master / district admin は全 visibility (hidden_all 含) read 可
 *   - update visibility: isDistrictAdmin(districtId) → admin は hidden_all への昇格も可
 *   - delete: isDistrictAdmin(districtId) → admin は他人の post も削除可 (REQ-4.3.9)
 *
 * Phase 5.5 で moderatePost callable + operationLogs 記録 (REQ-4.3.10) に置換予定 (R-20)。
 * 現状は client 直接 write、操作ログは未記録 (minimum scope)。
 */

export type PostsModerationState =
  | { status: 'loading'; items: []; error: null }
  | { status: 'ready'; items: ModerationPost[]; error: null }
  | { status: 'error'; items: []; error: Error };

/** 一覧表示用、UI で必要な field のみ。subscribe 元 `Post` から narrowing。 */
export interface ModerationPost {
  id: string;
  authorUid: string;
  authorNickname: string;
  title: string;
  comment: string | null;
  visibility: PostVisibility;
  createdAt: Date | null;
  photoPath: string | null;
  photoThumbPath: string | null;
  /** 原寸 download URL (docs/106、サムネ fallback)。一覧サムネ / 地図 / 詳細で getDownloadURL を省ける。 */
  photoUrl: string | null;
  /** サムネ download URL (docs/106)。一覧カードの写真サムネ用。 */
  photoThumbUrl: string | null;
  /** 写真の幾何メタ (docs/27、360度写真なら projection='equirectangular')。旧投稿は null。 */
  photo?: PostPhotoMeta | null;
  /** Storage に写真があるか (`photoPath` 有無、AI 再解析対象判定用)。 */
  hasPhoto: boolean;
  /** 位置 (地図マーカー用)。location が無い旧投稿は 0,0。 */
  latitude: number;
  longitude: number;
  aiStatus: PostAiAnalysis['status'] | null;
  archivedAt: Date | null;
  /** 投稿の入力項目の値 (extraFields)。 */
  extraFields: Record<string, unknown>;
  /** 旧 admin PostsModerationPage の AI 結果詳細表示 + chip 復元用 (Sprint BG-DataSettings 22)。 */
  aiAnalysisSettingsId: string | null;
  aiAnalysisEcw: PostAiAnalysis['ecw'] | null;
  aiAnalysisError: PostAiAnalysis['error'] | null;
  aiAnalysisAnalyzedAt: string | null;
  /** 解析結果の raw object (詳細 modal の details で生表示)。 */
  aiAnalysisRaw: Record<string, unknown> | null;
  /** 解析 mode (bousai / walker / ecw 等)。詳細 modal の分類テーブル分岐用。 */
  aiAnalysisMode: PostAiAnalysis['mode'] | null;
  /** 防災 (bousai) mode の result (詳細 modal の分類テーブル / 専門家の一言 / スコア用)。 */
  aiAnalysisBousai: AiAnalysisBousaiResult | null;
  /** ウォーカブル (walker) mode の result (詳細 modal の分類テーブル / 専門家の一言 / スコア用)。 */
  aiAnalysisWalker: AiAnalysisWalkerResult | null;
  /** AI 生成タイトル (extraFields.aiGeneratedTitle、詳細 modal「投稿概要」用)。 */
  aiGeneratedTitle: string | null;
  /** 検証集計キャッシュ (一覧 / 詳細の検証集計バー、検証フィルタ用)。 */
  verificationSummary: AiVerificationSummary | null;
  /** 写真差し替え履歴 (replacePostPhoto callable が append、詳細 modal で履歴表示)。 */
  photoReplacements: PostPhotoReplacement[];
  /** 写真の顔・ナンバーのぼかし処理の状態 (docs/28 §8-2)。導入前の投稿は null。 */
  photoPrivacy: PostPhotoPrivacy | null;
  /**
   * 投稿者からの AI 再解析の依頼 (`post.reanalysisRequest`、docs/13 §9-2)。依頼が無ければ null / 未定義。
   * 管理者が再解析に成功すると Functions が消す。既存のテスト用リテラル互換のため optional。
   */
  reanalysisRequest?: ModerationReanalysisRequest | null;
}

/** Functions が 1 日の AI 解析上限で解析を見送ったときの error.code (functions/src/ai/autoAnalyzePost.ts)。 */
export const AI_QUOTA_EXCEEDED_CODE = 'QUOTA_EXCEEDED';

/**
 * 一覧・詳細で使う AI 解析の状態の区分。`skipped` のうち 1 日の上限で見送ったものは `quota` に分ける
 * (どちらも画面には日本語の名前で出す)。
 */
export type ModerationAiKey = NonNullable<ModerationPost['aiStatus']> | 'unanalyzed' | 'quota';

/** pure: 投稿の AI 解析の状態の区分。 */
export function moderationAiKeyOf(
  p: Pick<ModerationPost, 'aiStatus' | 'aiAnalysisError'>,
): ModerationAiKey {
  if (p.aiStatus == null) return 'unanalyzed';
  if (p.aiStatus === 'skipped' && p.aiAnalysisError?.code === AI_QUOTA_EXCEEDED_CODE) {
    return 'quota';
  }
  return p.aiStatus;
}

/** AI 解析の状態の表示名 (一覧の絞り込み・バッジ・詳細で共通)。 */
export const MODERATION_AI_STATUS_LABEL: Record<ModerationAiKey, string> = {
  done: '解析済',
  pending: '待機中',
  processing: '処理中',
  failed: '失敗',
  skipped: '解析なし',
  quota: '上限で見送り',
  unanalyzed: '未解析',
};

/** 投稿者からの再解析の依頼 (一覧の絞り込み・詳細の表示用)。 */
export interface ModerationReanalysisRequest {
  requestedAt: Date | null;
  /** 解析の観点 (docs/29)。null = 既定の観点。 */
  surveyKey: string | null;
  /** 投稿者のひとこと (任意)。 */
  note: string | null;
}

/** pure: Firestore の `reanalysisRequest` を一覧用の形にする (依頼が無い・形が違うときは null)。 */
export function mapReanalysisRequest(raw: unknown): ModerationReanalysisRequest | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  // 書くのは Functions (requestPostReanalysis)。型は共有の PostReanalysisRequest だが、古い・壊れた
  // ドキュメントでも落ちないよう各項目を確かめてから使う。
  const r = raw as Partial<Record<keyof PostReanalysisRequest, unknown>>;
  const at = r.requestedAt as { toDate?: () => Date } | null | undefined;
  return {
    requestedAt: at && typeof at.toDate === 'function' ? at.toDate() : null,
    surveyKey: typeof r.surveyKey === 'string' && r.surveyKey ? r.surveyKey : null,
    note: typeof r.note === 'string' && r.note.trim() ? r.note.trim() : null,
  };
}

/**
 * `aiAnalysis.<mode>` (bousai / walker) を classifications を持つ result として取り出す。
 * old2 PostDetailModal L557-558 と同じく raw object を素通しで保持 (詳細 modal で
 * outputFormat 駆動の動的列テーブルに渡す)。classifications 配列が無ければ null。
 */
function pickClassificationResult<T>(
  aiRaw: Record<string, unknown> | null,
  modeKey: 'bousai' | 'walker',
): T | null {
  if (!aiRaw) return null;
  const v = aiRaw[modeKey];
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  return v as T;
}

function mapPost(snap: { id: string; data: () => DocumentData }): ModerationPost {
  const data = snap.data();
  const createdRaw = data.createdAt;
  const createdAt =
    createdRaw && typeof createdRaw.toDate === 'function' ? createdRaw.toDate() : null;
  const archivedRaw = data.archivedAt;
  const archivedAt =
    archivedRaw && typeof archivedRaw.toDate === 'function' ? archivedRaw.toDate() : null;
  const aiRaw =
    data.aiAnalysis && typeof data.aiAnalysis === 'object'
      ? (data.aiAnalysis as Record<string, unknown>)
      : null;
  const loc = data.location as { latitude?: number; longitude?: number } | undefined;
  const extraFields = ((data.extraFields as Record<string, unknown>) ?? {}) as Record<
    string,
    unknown
  >;
  const aiGeneratedTitleRaw = extraFields.aiGeneratedTitle;
  const photoPath = (data.photoPath as string | null) ?? null;
  return {
    id: snap.id,
    authorUid: (data.authorUid as string) ?? '',
    authorNickname: (data.authorNickname as string) ?? '(匿名)',
    title: (data.title as string) ?? '',
    comment: (data.comment as string | null) ?? null,
    visibility: ((data.visibility as PostVisibility) ?? 'public') satisfies PostVisibility,
    createdAt,
    photoPath,
    photoThumbPath: (data.photoThumbPath as string | null) ?? null,
    photoUrl: (data.photoUrl as string | null) ?? null,
    photoThumbUrl: (data.photoThumbUrl as string | null) ?? null,
    photo: (data.photo as PostPhotoMeta | null | undefined) ?? null,
    hasPhoto: !!photoPath,
    latitude: typeof loc?.latitude === 'number' ? loc.latitude : 0,
    longitude: typeof loc?.longitude === 'number' ? loc.longitude : 0,
    aiStatus:
      (aiRaw ? ((aiRaw as { status?: PostAiAnalysis['status'] }).status ?? null) : null) ?? null,
    archivedAt,
    extraFields,
    aiAnalysisSettingsId:
      (aiRaw ? ((aiRaw as { settingsId?: string | null }).settingsId ?? null) : null) ?? null,
    aiAnalysisEcw:
      (aiRaw ? ((aiRaw as { ecw?: PostAiAnalysis['ecw'] }).ecw ?? null) : null) ?? null,
    aiAnalysisError:
      (aiRaw ? ((aiRaw as { error?: PostAiAnalysis['error'] }).error ?? null) : null) ?? null,
    aiAnalysisAnalyzedAt:
      (aiRaw ? ((aiRaw as { analyzedAt?: string | null }).analyzedAt ?? null) : null) ?? null,
    aiAnalysisRaw: aiRaw,
    aiAnalysisMode:
      (aiRaw ? ((aiRaw as { mode?: PostAiAnalysis['mode'] }).mode ?? null) : null) ?? null,
    aiAnalysisBousai: pickClassificationResult<AiAnalysisBousaiResult>(aiRaw, 'bousai'),
    aiAnalysisWalker: pickClassificationResult<AiAnalysisWalkerResult>(aiRaw, 'walker'),
    aiGeneratedTitle:
      typeof aiGeneratedTitleRaw === 'string' && aiGeneratedTitleRaw.trim()
        ? aiGeneratedTitleRaw.trim()
        : null,
    verificationSummary:
      data.verificationSummary && typeof data.verificationSummary === 'object'
        ? (data.verificationSummary as AiVerificationSummary)
        : null,
    photoReplacements: Array.isArray(data.photoReplacements)
      ? (data.photoReplacements as PostPhotoReplacement[])
      : [],
    photoPrivacy:
      data.photoPrivacy && typeof data.photoPrivacy === 'object'
        ? (data.photoPrivacy as PostPhotoPrivacy)
        : null,
    reanalysisRequest: mapReanalysisRequest(data.reanalysisRequest),
  };
}

/**
 * 地区配下の posts を subscribe (admin moderation 用)。
 * 旧との差分: deletedAt フィルタを削除 (v2 では archivedAt 統一、orderBy createdAt desc)。
 */
export function usePostsModeration(districtId: string | null): PostsModerationState {
  const [state, setState] = useState<PostsModerationState>({
    status: 'loading',
    items: [],
    error: null,
  });
  useEffect(() => {
    if (!districtId) {
      setState({ status: 'error', items: [], error: new Error('districtId is required') });
      return;
    }
    const q = query(collection(db, 'districts', districtId, 'posts'), orderBy('createdAt', 'desc'));
    const unsub: Unsubscribe = onSnapshot(
      q,
      (snap) => {
        const items: ModerationPost[] = snap.docs.map(mapPost);
        setState({ status: 'ready', items, error: null });
      },
      (error) => setState({ status: 'error', items: [], error }),
    );
    return () => unsub();
  }, [districtId]);
  return state;
}

/**
 * visibility を変更 (admin 専用)。
 * `public` / `hidden_others` / `hidden_all` の 3 状態間で自由に切替可。
 * 操作ログは Phase 5 moderatePost callable で記録予定 (R-20)、現状は client 直接 write。
 */
export async function moderateVisibility(
  districtId: string,
  postId: string,
  visibility: PostVisibility,
  uid: string,
  reason?: string,
): Promise<void> {
  const ref = doc(db, 'districts', districtId, 'posts', postId);
  const payload: Record<string, unknown> = {
    visibility,
    moderatedBy: uid,
    moderatedAt: new Date().toISOString(),
    updatedAt: serverTimestamp(),
    updatedBy: uid,
  };
  if (reason !== undefined) payload.moderationReason = reason;
  await setDoc(ref, payload, { merge: true });
}

/** モデレーションで編集できる投稿内容 (公開範囲・写真は別経路、ここは本文・入力項目・位置)。 */
export interface ModerationContentEdit {
  title: string;
  comment: string | null;
  /** 位置 (地図ピン)。null のときは location を書き換えない (位置なし投稿を維持)。 */
  location: { latitude: number; longitude: number } | null;
  /** 地区 postFields 駆動の追加入力項目 (AI 書込値 aiGeneratedTitle 等は呼出側で温存)。 */
  extraFields: Record<string, unknown>;
}

/** Firestore は undefined を許容しないため、extraFields の undefined 値を除去する (pure)。 */
function stripUndefined(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

/**
 * 投稿内容 (タイトル / コメント / 入力項目 / 位置) を編集 (admin 専用)。
 *
 * 認可: firestore.rules `allow update: if isDistrictAdmin(districtId)` (visibility 変更と同経路の直接 write)。
 * 操作ログは Phase 5 moderatePost callable で記録予定 (R-20)、現状は moderatedBy/updatedBy で痕跡を残す。
 * extraFields は呼出側が「既存 extraFields に postFields の編集値を上書き」した全体を渡す前提
 * (AI 書込値 aiGeneratedTitle / provenance importedFrom は温存される)。merge:true でも全 key を渡すため整合。
 */
export async function moderatePostContent(
  districtId: string,
  postId: string,
  content: ModerationContentEdit,
  uid: string,
): Promise<void> {
  const ref = doc(db, 'districts', districtId, 'posts', postId);
  const trimmedComment = content.comment?.trim() ? content.comment : null;
  const payload: Record<string, unknown> = {
    title: content.title.trim(),
    comment: trimmedComment,
    extraFields: stripUndefined(content.extraFields),
    moderatedBy: uid,
    moderatedAt: new Date().toISOString(),
    updatedAt: serverTimestamp(),
    updatedBy: uid,
  };
  if (content.location) {
    payload.location = {
      latitude: content.location.latitude,
      longitude: content.location.longitude,
    };
  }
  await setDoc(ref, payload, { merge: true });
}

/**
 * post を物理削除 (admin 専用、REQ-4.3.9 完全削除)。
 * 旧 v1 は deletedAt 論理削除 + Storage path 削除 callable。v2 は rules で
 * `allow delete: if isDistrictAdmin` 許可済、操作ログは Phase 5 で記録 (R-20)。
 *
 * 注意: Storage 写真 (`photoPath` / `photoThumbPath`) は本関数では削除しない。
 * 写真削除は Phase 5 moderatePost callable で trigger 連動予定。
 */
export async function deletePost(districtId: string, postId: string): Promise<void> {
  const ref = doc(db, 'districts', districtId, 'posts', postId);
  await deleteDoc(ref);
}
